<template>
  <div class="proxy-tester-page dark-theme">
    <div class="proxy-container">
      <!-- 页面标题 -->
      <div class="page-header">
        <div class="header-icon">
          <a-icon type="api" />
        </div>
        <div class="header-text">
          <h2 class="page-title">{{ translate('proxy_test') }}</h2>
          <p class="page-description">{{ translate('proxy_test_description') }}</p>
        </div>
      </div>
      
      <!-- 主内容区 -->
      <div class="main-content">
        <!-- 顶部配置区域 -->
        <div class="top-section">
          <!-- 左侧说明卡片 -->
          <div class="section-card instructions-card">
            <div class="card-header">
              <a-icon type="info-circle" class="card-icon" />
              <h3 class="card-title">{{ translate('instructions') }}</h3>
            </div>
            <div class="instructions-content">
              <ol>
                <li>{{ translate('proxy_instruction_1') }}</li>
                <li>{{ translate('proxy_instruction_2') }}</li>
                <li>{{ translate('proxy_instruction_3') }}</li>
              </ol>
              <div class="format-info">
                <p class="format-label">{{ translate('supported_format') }}：</p>
                <p class="format-example">192.168.0.1:8000:{{ translate('proxy_account') }}:{{ translate('proxy_password') }}</p>
              </div>
            </div>
          </div>
          
          <!-- 右侧表单卡片 -->
          <div class="section-card form-card">
            <div class="card-header">
              <a-icon type="setting" class="card-icon" />
              <h3 class="card-title">{{ translate('proxy_config') }}</h3>
            </div>
            <a-form :model="form" ref="form" layout="vertical" class="form-container">
              <div class="form-row">
                <div class="form-group">
                  <a-form-item :label="translate('ip_query_channel')">
                    <a-select v-model="form.ipQueryChannel" :placeholder="translate('please_select')" class="form-control">
                      <a-select-option
                        v-for="item in ipQueryChannels"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </div>
                <div class="form-group">
                  <a-form-item :label="translate('proxy_type')">
                    <a-select v-model="form.proxyType" :placeholder="translate('please_select')" class="form-control">
                      <a-select-option
                        v-for="item in proxyTypes"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </div>
              </div>
              <div class="form-row full-width">
                <div class="form-group full-width">
                  <a-form-item :label="translate('proxy_info')">
                    <a-textarea
                      v-model="form.proxyInfo"
                      @change="onChange"
                      :placeholder="translate('please_input_content')"
                      :rows="6"
                      class="form-control"
                    />
                  </a-form-item>
                </div>
              </div>
              <div class="form-actions">
                <a-button type="primary" class="action-btn" @click="checkProxy">
                  {{ translate('check_proxy') }}
                </a-button>
              </div>
            </a-form>
          </div>
        </div>
        
        <!-- 底部表格区域 -->
        <div class="bottom-section">
          <div class="section-card table-card">
            <div class="card-header">
              <a-icon type="table" class="card-icon" />
              <h3 class="card-title">{{ translate('proxy_result') }}</h3>
            </div>
            <a-table
              :columns="columns"
              :data-source="proxyList"
              :row-key="(record, index) => `${record.ip}:${record.port}:${index}`"
              :pagination="false"
              class="dark-table"
            >
              <template slot="status" slot-scope="cell, record">
                <template v-if="record">
                  <div v-if="record.loading">
                    <a-spin size="small" />
                  </div>
                  <div v-else class="status-indicator" :class="getStatusClass(record.status)">
                    {{ record.status }}
                  </div>
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  inject: ['languageState'],
  data() {
    // 使用默认语言初始化，避免computed-in-data问题
    const langTranslations = {
      en: {
        proxy_test: 'Proxy Test',
        proxy_test_description: 'Please fill in the proxy information and click the Check Proxy button to test proxy availability',
        instructions: 'Instructions',
        proxy_instruction_1: 'If proxy type is not filled in, it will be added according to the set proxy type',
        proxy_instruction_2: 'Only HTTP and Socks5 proxy types are supported',
        proxy_instruction_3: 'One proxy per line, up to 20 at a time',
        supported_format: 'Supported format',
        proxy_account: 'proxy_account',
        proxy_password: 'proxy_password',
        ip_query_channel: 'IP Query Channel',
        proxy_type: 'Proxy Type',
        proxy_info: 'Proxy Information',
        check_proxy: 'Check Proxy',
        please_select: 'Please select',
        please_input_content: 'Please input content',
        proxy_type_col: 'Proxy Type',
        proxy_host_col: 'Proxy Host',
        proxy_port_col: 'Proxy Port',
        proxy_account_col: 'Proxy Account',
        proxy_password_col: 'Proxy Password',
        ip_channel_col: 'IP Channel',
        exit_ip_col: 'Exit IP',
        status_col: 'Status',
        unchecked: 'Unchecked',
        checking: 'Checking',
        available: 'Available',
        unavailable: 'Unavailable',
        check_failed: 'Check Failed',
        proxy_config: 'Proxy Configuration',
        proxy_result: 'Proxy Test Results'
      },
      zh: {
        proxy_test: '代理测试',
        proxy_test_description: '请填写代理信息，点击检查代理按钮测试代理可用性',
        instructions: '说明',
        proxy_instruction_1: '不填写代理类型时，将按照设置的代理类型进行添加',
        proxy_instruction_2: '代理类型仅支持HTTP、Socks5',
        proxy_instruction_3: '一行一个代理，一次最多添加20个',
        supported_format: '支持以下填写格式',
        proxy_account: '代理账号',
        proxy_password: '代理密码',
        ip_query_channel: 'IP查询渠道',
        proxy_type: '代理类型',
        proxy_info: '代理信息',
        check_proxy: '检查代理',
        please_select: '请选择',
        please_input_content: '请输入内容',
        proxy_type_col: '代理类型',
        proxy_host_col: '代理主机',
        proxy_port_col: '代理端口',
        proxy_account_col: '代理账号',
        proxy_password_col: '代理密码',
        ip_channel_col: 'IP查询渠道',
        exit_ip_col: '出口IP',
        status_col: '状态',
        unchecked: '未检查',
        checking: '正在检查',
        available: '可用',
        unavailable: '不可用',
        check_failed: '检查失败',
        proxy_config: '代理配置',
        proxy_result: '代理测试结果'
      }
    };
    return {
      form: {
        ipQueryChannel: 'IPinfo',
        proxyType: 'Socks5',
        proxyInfo: ''
      },
      langTranslations,
      proxyList: [],
      ipQueryChannels: [
        { value: 'IPinfo', label: 'IPinfo' }
      ],
      proxyTypes: [
        { value: 'Socks5', label: 'Socks5' },
        { value: 'HTTP', label: 'HTTP' }
      ],
      loading: false
    };
  },
  computed: {
    currentLanguage() {
      return this.languageState?.languageCode || 'en';
    },
    columns() {
      return [
        { title: this.translate('proxy_type_col'), dataIndex: 'proxytype', key: 'proxytype', width: 100 },
        { title: this.translate('proxy_host_col'), dataIndex: 'ip', key: 'ip', width: 100 },
        { title: this.translate('proxy_port_col'), dataIndex: 'port', key: 'port', width: 100 },
        { title: this.translate('proxy_account_col'), dataIndex: 'account', key: 'account', width: 100 },
        { title: this.translate('proxy_password_col'), dataIndex: 'password', key: 'password', width: 100 },
        { title: this.translate('ip_channel_col'), dataIndex: 'ipchanel', key: 'ipchanel', width: 100 },
        { title: this.translate('exit_ip_col'), dataIndex: 'ipout', key: 'ipout', width: 100 },
        { title: this.translate('status_col'), dataIndex: 'status', key: 'status', width: 100, scopedSlots: { customRender: 'status' } }
      ];
    }
  },
  watch: {
    'form.proxyType': function(newType) {
      this.proxyList.forEach(proxy => {
        this.$set(proxy, 'proxytype', newType);
      });
    }
  },
  methods: {
    getStatusClass(status) {
      if (status === this.translate('available')) return 'status-success';
      if (status === this.translate('unavailable')) return 'status-error';
      if (status === this.translate('checking')) return 'status-warning';
      if (status === this.translate('unchecked')) return 'status-default';
      if (status === this.translate('check_failed')) return 'status-error';
      return 'status-default';
    },
    translate(key) {
      const lang = this.currentLanguage;
      return this.langTranslations[lang][key] || key;
    },
    parseProxyString(str) {
      const parts = str.split(':');
      const host = parts[0];
      const port = parts[1];
      const account = parts[2] || '';
      const password = parts[3] || '';

      return {
        proxytype: this.form.proxyType,
        ip: host,
        port: port,
        account: account || '-',
        password: password || '-',
        ipchanel: this.form.ipQueryChannel,
        ipout: '-',
        status: this.translate('unchecked'),
        loading: false
      };
    },
    onChange(e) {
      const value = e.target.value;
      const lines = value.split('\n').filter(line => line.trim() !== '');
      this.proxyList = lines.map(this.parseProxyString).slice(0, 20);
    },
    async checkProxy() {
      for (let i = 0; i < this.proxyList.length; i++) {
        const proxy = this.proxyList[i];
        this.$set(proxy, 'loading', true);
        this.$set(proxy, 'status', this.translate('checking'));
        try {
          const res = await this.$request.post("/checkproxy/check", {
            proxyPort: proxy.port,
            proxyHost: proxy.ip,
            username: proxy.account,
            password: proxy.password,
            proxytype: proxy.proxytype
          });
          if (res.data.code === 200) {
            this.$set(proxy, 'status', this.translate('available'));
            const eachData = JSON.parse(res.data.data);
            this.$set(proxy, 'ipout', `${eachData.ip}\n${eachData.country_code}\n${eachData.country_name}`);
          } else {
            this.$set(proxy, 'status', this.translate('unavailable'));
          }
        } catch (error) {
          console.error('检查代理失败', error);
          this.$set(proxy, 'status', this.translate('check_failed'));
        } finally {
          this.$set(proxy, 'loading', false);
        }
      }
    }
  }
};
</script>

<style lang="less" scoped>
// 深色主题全局样式
.dark-theme {
  background: #2d2c28;
  color: #ffffff;
  // min-height: 100vh;
}

// 主容器样式
.proxy-tester-page {
  padding: 20px;
  min-height: 100vh;
}

.proxy-container {
  max-width: 1400px;
  margin: 0 auto;
}

// 页面标题
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  margin-bottom: 24px;
  background: #33342e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.header-icon {
  width: 56px;
  height: 56px;
  background: rgba(0, 234, 144, 0.1);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .anticon {
    font-size: 28px;
    color: #00ea90;
  }
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 26px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 6px 0;
  letter-spacing: 0.5px;
}

.page-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.6;
}

// 主内容区
.main-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// 顶部区域
.top-section {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
}

// 卡片样式
.section-card {
  background: #33342e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(21, 129, 198, 0.3);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
}

// 卡片头部
.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.card-icon {
  font-size: 20px;
  color: #00ea90;
  background: rgba(0, 234, 144, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.card-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
}

// 说明内容
.instructions-content {
  color: rgba(255, 255, 255, 0.8);
  
  ol {
    margin: 0 0 20px 0;
    padding-left: 0;
    list-style: none;
    counter-reset: item;
    
    li {
      margin-bottom: 16px;
      line-height: 1.6;
      font-size: 14px;
      position: relative;
      padding-left: 32px;
      
      &::before {
        content: counter(item);
        counter-increment: item;
        position: absolute;
        left: 0;
        top: 2px;
        width: 20px;
        height: 20px;
        background: rgba(0, 234, 144, 0.15);
        color: #00ea90;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 700;
      }
    }
  }
}

.format-info {
  margin-top: 24px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border-left: 4px solid #00ea90;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);
}

.format-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    background: #00ea90;
    border-radius: 50%;
  }
}

.format-example {
  font-size: 13px;
  color: #00ea90;
  font-family: 'Fira Code', 'Courier New', monospace;
  background: #1f1e1b;
  padding: 12px;
  border-radius: 8px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  line-height: 1.5;
}

// 表单样式
.form-card {
  flex: 1;
}

.form-container {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  
  &.full-width {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
}

.form-group {
  flex: 1;
  
  &.full-width {
    flex: none;
    width: 100%;
  }
}

.form-control {
  width: 100%;
  
  &.ant-select,
  &.ant-input,
  &.ant-input-affix-wrapper {
    background: rgba(255, 255, 255, 0.02) !important;
    border-radius: 12px !important;
    color: #ffffff !important;
    font-size: 15px !important;
    transition: all 0.3s ease !important;
  }

  &.ant-input {
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    padding: 12px !important;
    box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.1) !important;
    line-height: 1.6 !important;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.4) !important;
    }
    
    &:focus,
    &:hover {
      border-color: #00ea90 !important;
      background: rgba(255, 255, 255, 0.04) !important;
      box-shadow: 0 0 0 2px rgba(0, 234, 144, 0.15) !important;
    }
  }

  &.ant-select {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    
    :deep(.ant-select-selection) {
      background: rgba(255, 255, 255, 0.02) !important;
      border: 1px solid rgba(255, 255, 255, 0.08) !important;
      border-radius: 12px !important;
      color: #ffffff !important;
      height: 42px !important;
      box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.1) !important;
      display: flex;
      align-items: center;
      transition: all 0.3s ease !important;
      
      .ant-select-selection__rendered {
        line-height: 40px !important;
        margin-left: 12px;
      }
      
      &:focus,
      &:hover {
        border-color: #00ea90 !important;
        background: rgba(255, 255, 255, 0.04) !important;
        box-shadow: 0 0 0 2px rgba(0, 234, 144, 0.15), inset 0 2px 6px rgba(0, 0, 0, 0.1) !important;
      }
    }
    
    :deep(.ant-select-selection-item),
    :deep(.ant-select-selection-selected-value) {
      color: #ffffff !important;
      line-height: 40px !important;
    }
  
    :deep(.ant-select-selection__placeholder) {
      color: rgba(255, 255, 255, 0.4) !important;
      line-height: 40px !important;
    }
  }
}

:deep(.ant-select-dropdown) {
  background: #2d2c28 !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
  padding: 8px !important;
}

:deep(.ant-select-item-option),
:deep(.ant-select-dropdown-menu-item) {
  color: rgba(255, 255, 255, 0.8) !important;
  border-radius: 8px !important;
  margin-bottom: 4px !important;
  padding: 8px 12px !important;
  transition: all 0.2s ease !important;
  
  &.ant-select-item-option-selected,
  &.ant-select-dropdown-menu-item-selected {
    background: rgba(0, 234, 144, 0.15) !important;
    color: #00ea90 !important;
    font-weight: 600 !important;
  }
  
  &:hover,
  &.ant-select-dropdown-menu-item-active {
    background: rgba(255, 255, 255, 0.05) !important;
    color: #ffffff !important;
  }
}

// Form label style
:deep(.ant-form-item-label > label) {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8) !important;
  padding: 0 !important;
  margin-right: 0;
  letter-spacing: 0.5px;
}

// Ant Design form item label text
:deep(.ant-form-item-label) {
  color: #ffffff !important;
}

// Ant Design label component
:deep(.ant-form-item-label label) {
  color: #ffffff !important;
}

.form-actions {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.action-btn {
  height: 48px;
  font-size: 15px;
  font-weight: 600;
  padding: 0 36px;
  border-radius: 12px !important;
  line-height: 48px !important;
  background: #00ea90 !important;
  border: none !important;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #1a1a1a !important;
  box-shadow: 0 4px 12px rgba(0, 234, 144, 0.2) !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 234, 144, 0.3) !important;
    background: #00ff9d !important;
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 234, 144, 0.2) !important;
  }
}

// 底部表格区域
.bottom-section {
  margin-top: 0;
}

// 深色表格样式
.dark-table {
  width: 100%;
  background: transparent !important;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  :deep(.ant-table) {
    background: transparent !important;
    color: #ffffff !important;
  }
  
  :deep(.ant-table-container) {
    background: transparent !important;
  }
  
  :deep(.ant-table-thead > tr > th) {
    background: rgba(255, 255, 255, 0.02) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    color: rgba(255, 255, 255, 0.6) !important;
    font-weight: 600 !important;
    font-size: 13px;
    padding: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  :deep(.ant-table-tbody > tr) {
    background: transparent !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03) !important;
    transition: all 0.2s ease;
    
    &:last-child {
      border-bottom: none !important;
      
      > td {
        border-bottom: none !important;
      }
    }
  }
  
  :deep(.ant-table-tbody > tr > td) {
    color: rgba(255, 255, 255, 0.8) !important;
    font-size: 14px;
    padding: 16px;
    background: transparent !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03) !important;
  }
  
  :deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(0, 234, 144, 0.05) !important;
  }
  
  // 表格空状态样式
  :deep(.ant-empty) {
    color: rgba(255, 255, 255, 0.5) !important;
    background: transparent !important;
    border: none !important;
    padding: 60px 20px !important;
    margin: 0 !important;
  }
  
  :deep(.ant-empty-image svg) {
    fill: rgba(255, 255, 255, 0.1) !important;
    stroke: rgba(255, 255, 255, 0.2) !important;
    height: 80px;
  }
  
  :deep(.ant-empty-description) {
    color: rgba(255, 255, 255, 0.4) !important;
    font-size: 14px;
    margin-top: 16px;
  }
  
  :deep(.ant-table-placeholder) {
    background: rgba(255, 255, 255, 0.01) !important;
    border-bottom: none !important;
  }
}

// 状态指示器
.status-indicator {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  min-width: 80px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &.status-success {
    background: rgba(0, 234, 144, 0.15);
    color: #00ea90;
    border: 1px solid rgba(0, 234, 144, 0.3);
  }
  
  &.status-error {
    background: rgba(245, 34, 45, 0.15);
    color: #ff4d4f;
    border: 1px solid rgba(245, 34, 45, 0.3);
  }
  
  &.status-warning {
    background: rgba(250, 173, 20, 0.15);
    color: #faad14;
    border: 1px solid rgba(250, 173, 20, 0.3);
  }
  
  &.status-default {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.15);
  }
}

// 响应式设计
@media (max-width: 768px) {
  .proxy-tester-page {
    padding: 16px;
  }
  
  .page-header {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .header-icon {
    width: 48px;
    height: 48px;
    
    .anticon {
      font-size: 24px;
    }
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .top-section {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    flex-direction: column;
    gap: 16px;
  }
  
  .form-actions {
    justify-content: center;
  }
  
  .action-btn {
    width: 100%;
  }
}
</style>